import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { AnimatedText, Reveal } from '../../../motion';
import { fadeUp } from '../../../motion/variants';
import { grey } from '../../../theme/tokens';
import { headline, prose, red, stencil } from './garage';

interface GarageHeadingProps {
  /** Stencilled label above the title, behind a short red bar. */
  eyebrow: string;
  /** Newlines set the line breaks; without any, lines balance on their own. */
  title: string;
  /** Words of the title set in red. */
  highlight?: string[];
  description?: string;
  /**
   * `left` and `center` stack the description under the title; `split` puts it
   * beside the title on wide screens, so a full-width section does not leave
   * half its header empty.
   */
  layout?: 'left' | 'center' | 'split';
  /** On a steel band, which stays dark in both colour modes. */
  onSteel?: boolean;
}

/** Section heading for the home page, in condensed sign lettering. */
const GarageHeading: React.FC<GarageHeadingProps> = ({
  eyebrow,
  title,
  highlight,
  description,
  layout = 'left',
  onSteel = false,
}) => {
  const theme = useTheme();
  const accent = red(onSteel || theme.palette.mode === 'dark');
  const centered = layout === 'center';
  const split = layout === 'split';

  const titleBlock = (
    <Box>
      <Reveal variants={fadeUp}>
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent={centered ? 'center' : 'flex-start'}
          sx={{ mb: 1.75 }}
        >
          <Box aria-hidden sx={{ width: 28, height: 4, backgroundColor: accent }} />
          <Typography component="p" sx={{ ...stencil, color: accent }}>
            {eyebrow}
          </Typography>
        </Stack>
      </Reveal>

      <AnimatedText
        component="h2"
        text={title}
        by={title.includes('\n') ? 'line' : 'word'}
        highlight={highlight}
        highlightSx={{ color: accent }}
        whenInView
        sx={{
          ...headline,
          fontSize: { xs: '2.75rem', md: '4rem' },
          color: onSteel ? grey[50] : 'text.primary',
        }}
      />
    </Box>
  );

  const descriptionBlock = description && (
    <Reveal variants={fadeUp} delay={0.1}>
      <Typography
        sx={{
          ...prose,
          // Short centred copy reads best as even lines.
          ...(centered && { textWrap: 'balance' }),
          mt: split ? { xs: 2, md: 0 } : 2,
          fontSize: '1.0625rem',
          lineHeight: 1.65,
          color: onSteel ? grey[300] : 'text.secondary',
          maxWidth: '52ch',
          mx: centered ? 'auto' : 0,
        }}
      >
        {description}
      </Typography>
    </Reveal>
  );

  if (split) {
    return (
      <Box
        sx={{
          mb: { xs: 4, md: 7 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
          columnGap: 8,
          alignItems: 'end',
        }}
      >
        {titleBlock}
        {descriptionBlock}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: { xs: 4, md: 6 },
        textAlign: centered ? 'center' : 'left',
        maxWidth: centered ? '50rem' : '40rem',
        mx: centered ? 'auto' : 0,
      }}
    >
      {titleBlock}
      {descriptionBlock}
    </Box>
  );
};

export default GarageHeading;
